// NoOrgAccess — shown when a user is authenticated but belongs to no org.
//
// Replaces the old self-signup Onboarding (org creation removed per the
// admin/invite-provisioned model). A user gets into an org via:
//   1. An explicit invitation (super-admin or org owner)
//   2. Domain auto-join (handled server-side on login; if it applied,
//      this screen never shows)
// If neither applies, they can REQUEST an org → super-admin approves.

import { useState } from 'react';
import { Building2, Mail, Send, Loader2 } from 'lucide-react';
import { useAuth } from '@govern360/shared';
import { requestOrg } from './lib/orgRequestApi.js';

export default function NoOrgAccess() {
  const { user, logout } = useAuth();
  const [orgName, setOrgName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!orgName.trim()) { setError('Organization name is required.'); return; }
    setBusy(true);
    const { error: err } = await requestOrg(orgName.trim(), note.trim());
    setBusy(false);
    if (err) { setError(err.message || 'Could not submit request.'); return; }
    setSent(true);
  }

  return (
    <div className="login-wrap">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--g-primary-50)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <Building2 size={22} style={{ color: 'var(--g-primary)' }} />
        </div>

        {sent ? (
          <>
            <h1 className="login-h1">Request submitted</h1>
            <p className="login-sub">
              Thanks, {user?.name}. Your request for <strong>{orgName}</strong> has been sent to the
              Govern360 team for approval. You'll get access once it's approved — sign in again then.
            </p>
            <button className="btn outline block" style={{ marginTop: 8 }}
              onClick={() => logout()}>Sign out</button>
          </>
        ) : (
          <>
            <h1 className="login-h1">No organization access</h1>
            <p className="login-sub">
              You're signed in as <strong>{user?.email}</strong>, but you don't belong to a Govern360
              organization yet. If your company already uses Govern360, ask an admin to invite you
              (or sign in with your work email so we can match your domain). Otherwise, request a new
              organization below and our team will set it up.
            </p>

            <div className="banner" style={{ marginBottom: 16 }}>
              <Mail size={18} />
              <span style={{ fontSize: 12 }}>Have an invite link? Open it directly — it attaches you to the right org.</span>
            </div>

            <form onSubmit={submit}>
              <div style={{ marginBottom: 14 }}>
                <label className="field-l">Organization name</label>
                <input className="inp" value={orgName} onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Inc." required />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="field-l">Note (optional)</label>
                <input className="inp" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything our team should know" />
              </div>
              {error && <p style={{ color: 'var(--g-danger)', fontSize: 13, marginBottom: 8 }}>{error}</p>}
              <button type="submit" className="btn primary block" style={{ height: 44 }} disabled={busy}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Request organization
              </button>
            </form>

            <p className="login-foot">
              Wrong account?{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); logout(); }}>Sign out</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
